/**
 * Beneficio Recuperado — "Lista de Ingresos x Fecha (Recuperado)" del
 * escritorio, mejorado.
 *
 * Por rango de FECHA DE PAGO, una fila por financiamiento de motor que
 * recibió dinero, con la ganancia REALIZADA en el período:
 *   recuperado = capital del vehículo cobrado * beneficio / precio
 *   total rec. = recuperado + interés + mora
 * El capital del vehículo incluye los abonos a capital y excluye lo que se
 * fue a cargos financiados en las cuotas (matrícula). Además del período
 * muestra el avance de cada expediente (beneficio ya recuperado, contando
 * la parte que aportó el inicial) y marca los costos dudosos y los
 * refinanciados. Todo el cálculo vive en el backend
 * (/api/beneficiorecuperado); aquí solo se presenta.
 *
 * Programa propio "beneficiorecuperado": expone costos y márgenes.
 */
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import {
  FP,
  PageHeader,
  StatCard,
  StatusPill,
  fpInputSx,
  fpDataGridSx,
} from "components/financepro";
import { useApi, usePerfilSucursal, useSucursales } from "lib/api";
import { fmtMoney } from "lib/moneda";
import { tienePermisosModulo, puedeAccionPrograma } from "lib/seguridad/permisosPorModulo";
import { imprimirListadoGrande, imprimirListadoAgrupado } from "lib/impresionhtml";

const PROGRAMA = "beneficiorecuperado";

const hoyISO = () => new Date().toISOString().slice(0, 10);
const fechaDMY = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin datos — procese el reporte
      </Typography>
    </Stack>
  );
}

const moneyCol = (field, headerName, width = 112, extra = {}) => ({
  field,
  headerName,
  width,
  type: "number",
  align: "right",
  headerAlign: "right",
  valueFormatter: (value) => fmtMoney(value),
  ...extra,
});

// Avance = beneficio ya recuperado (inicial incluido) sobre el beneficio total.
export const avancePct = (fila) => {
  const beneficio = Number(fila?.beneficio) || 0;
  if (beneficio <= 0) return 0;
  return Math.max(0, Math.min(100, ((Number(fila.acumulado) || 0) / beneficio) * 100));
};

const AGRUPADORES = {
  sucursal: {
    etiqueta: "Sucursal",
    clave: (f) =>
      f.sucursal
        ? `${f.sucursal}${f.sucursaldescrip ? ` - ${f.sucursaldescrip}` : ""}`
        : "Sin sucursal",
  },
  apertura: {
    etiqueta: "Mes de apertura",
    // aperturaiso = "2025-04-11" -> "2025-04": ordena bien como texto.
    clave: (f) => String(f.aperturaiso || "").slice(0, 7) || "Sin fecha",
  },
};

const CABECERAS = [
  "Expediente",
  "Cliente",
  "Apertura",
  "Costo",
  "Precio Venta",
  "Beneficio",
  "Recuperado",
  "Interés",
  "Mora",
  "Total Rec.",
];
const NUMERICAS = [3, 4, 5, 6, 7, 8, 9];
const filaImpresa = (f) => [
  f.noprest,
  `${f.cliente}${f.refinanciado ? " (refin.)" : ""}${f.costodudoso ? " *" : ""}`,
  f.apertura,
  fmtMoney(f.costo),
  fmtMoney(f.precio),
  fmtMoney(f.beneficio),
  fmtMoney(f.recuperado),
  fmtMoney(f.interes),
  fmtMoney(f.mora),
  fmtMoney(f.totalrec),
];
const suma = (filas, campo) => filas.reduce((a, f) => a + (Number(f[campo]) || 0), 0);
const filaTotal = (etiqueta, filas) => [
  "",
  etiqueta,
  "",
  "",
  "",
  fmtMoney(suma(filas, "beneficio")),
  fmtMoney(suma(filas, "recuperado")),
  fmtMoney(suma(filas, "interes")),
  fmtMoney(suma(filas, "mora")),
  fmtMoney(suma(filas, "totalrec")),
];

export default function BeneficioRecuperado() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const navigate = useNavigate();
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;
  // Alcance de sucursal: admin / `versucursales`, o el checkbox "Ver
  // sucursal" de este programa en el rol. El backend fuerza lo mismo.
  const { esadmin, sucursalPropia } = usePerfilSucursal();
  const veSucursales = esadmin || puedeAccionPrograma(roleDetalles, PROGRAMA, "versucursal");
  const puedeExpediente = tienePermisosModulo(roleDetalles, "expediente");

  const [fechaDesde, setFechaDesde] = useState(hoyISO());
  const [fechaHasta, setFechaHasta] = useState(hoyISO());
  const [sucursal, setSucursal] = useState("");
  const sucursales = useSucursales({ todas: veSucursales });
  const [carteras, setCarteras] = useState([]);
  const [idcartera, setIdcartera] = useState("");
  const [sinRefinanciados, setSinRefinanciados] = useState(false);
  const [agrupar, setAgrupar] = useState("");

  const [filas, setFilas] = useState([]);
  const [totales, setTotales] = useState(null);
  const [hayLibroCargos, setHayLibroCargos] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    post("/api/prestamopersonal_config", {}).then(({ ok, data }) => {
      if (ok) setCarteras(data.carteras || []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sin alcance multi-sucursal queda fijado a la suya (con la etiqueta del
  // catálogo cuando ya cargó, para que el selector la muestre completa).
  useEffect(() => {
    if (veSucursales || !sucursalPropia) return;
    const propia = sucursales.find(
      (s) =>
        String(s.label || "")
          .split("-")[0]
          .trim() === sucursalPropia
    );
    setSucursal(propia ? propia.label : sucursalPropia);
  }, [veSucursales, sucursalPropia, sucursales]);

  const procesar = async () => {
    if (!fechaDesde || !fechaHasta) {
      showAlert({ message: "Debe indicar el rango de fechas", color: "warning" });
      return;
    }
    if (fechaDesde > fechaHasta) {
      showAlert({
        message: "La fecha desde no puede ser mayor que la fecha hasta",
        color: "warning",
      });
      return;
    }
    setLoading(true);
    try {
      const { ok, data } = await post("/api/beneficiorecuperado", {
        fechadesde: fechaDesde,
        fechahasta: fechaHasta,
        // El backend espera el código puro ("2"), no la etiqueta "2-Terrenas".
        sucursal: String(sucursal || "")
          .split("-")[0]
          .trim(),
        idcartera: idcartera || "",
        sinrefinanciados: sinRefinanciados,
      });
      if (ok) {
        setFilas(data.filas || []);
        setTotales(data.totales || null);
        setHayLibroCargos(data.haylibrocargos !== false);
      } else {
        showAlert({ message: data.Error || "No se pudo generar el reporte", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const carteraDescrip = (id) => {
    const c = carteras.find((k) => String(k.id) === String(id));
    return c ? c.descripcion : "";
  };

  const rangoTexto =
    fechaDesde === fechaHasta
      ? `Pagos del ${fechaDMY(fechaDesde)}`
      : `Pagos del ${fechaDMY(fechaDesde)} al ${fechaDMY(fechaHasta)}`;
  const subtitulo = `${rangoTexto}${sucursal ? ` · Sucursal ${sucursal}` : ""}${
    idcartera ? ` · Cartera ${carteraDescrip(idcartera)}` : ""
  }${sinRefinanciados ? " · Sin refinanciados" : ""}`;

  const grupos = useMemo(() => {
    const def = AGRUPADORES[agrupar];
    if (!def) return [];
    const mapa = new Map();
    filas.forEach((f) => {
      const k = def.clave(f);
      if (!mapa.has(k)) mapa.set(k, []);
      mapa.get(k).push(f);
    });
    return [...mapa.entries()]
      .sort(([a], [b]) => a.localeCompare(b, "es", { numeric: true }))
      .map(([titulo, lista]) => ({ titulo, lista }));
  }, [agrupar, filas]);

  const exportar = () => {
    const def = AGRUPADORES[agrupar];
    const hoja = XLSX.utils.aoa_to_sheet([
      ["Beneficio Recuperado"],
      [subtitulo],
      [],
      [
        "Expediente",
        "Cliente",
        "Cédula",
        "Chasis",
        "Sucursal",
        ...(def ? [def.etiqueta] : []),
        "Apertura",
        "Costo",
        "Precio Venta",
        "Beneficio",
        "Margen %",
        "Capital vehículo",
        "Cargos en cuota",
        "Recuperado",
        "Interés",
        "Mora",
        "Total Rec.",
        "Efectivo cobrado",
        "Recuperado acumulado",
        "Beneficio pendiente",
        "Avance %",
        "Refinanciado",
        "Costo dudoso",
      ],
      ...filas.map((f) => [
        f.noprest,
        f.cliente,
        f.cedula,
        f.chasis,
        f.sucursal,
        ...(def ? [def.clave(f)] : []),
        f.apertura,
        f.costo,
        f.precio,
        f.beneficio,
        f.margen,
        f.capital,
        f.cargos,
        f.recuperado,
        f.interes,
        f.mora,
        f.totalrec,
        f.cobrado,
        f.acumulado,
        f.pendiente,
        Math.round(avancePct(f) * 100) / 100,
        f.refinanciado ? "Sí" : "",
        f.costodudoso ? "Sí" : "",
      ]),
      [],
      [
        "",
        "TOTAL",
        "",
        "",
        "",
        ...(def ? [""] : []),
        "",
        "",
        "",
        totales?.beneficio || 0,
        "",
        totales?.capital || 0,
        totales?.cargos || 0,
        totales?.recuperado || 0,
        totales?.interes || 0,
        totales?.mora || 0,
        totales?.totalrec || 0,
        totales?.cobrado || 0,
      ],
    ]);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Beneficio");
    XLSX.writeFile(libro, `BeneficioRecuperado_${fechaDesde}_${fechaHasta}.xlsx`);
  };

  const imprimir = () => {
    const comun = {
      titulo: "Beneficio Recuperado",
      subtitulo: `${subtitulo}${
        totales?.costodudoso ? " · (*) costo dudoso: beneficio posiblemente inflado" : ""
      }`,
      cabeceras: CABECERAS,
      totales: filaTotal("TOTAL", filas),
      numericas: NUMERICAS,
    };
    if (grupos.length) {
      imprimirListadoAgrupado({
        ...comun,
        pagina: "letter landscape",
        grupos: grupos.map((g) => ({
          titulo: g.titulo,
          resumen: `${g.lista.length} expediente(s) · Total rec. ${fmtMoney(
            suma(g.lista, "totalrec")
          )}`,
          filas: g.lista.map(filaImpresa),
          totales: filaTotal("Subtotal", g.lista),
        })),
      });
    } else {
      imprimirListadoGrande({ ...comun, filas: filas.map(filaImpresa) });
    }
  };

  const columnas = [
    {
      field: "noprest",
      headerName: "Expediente",
      width: 100,
      renderCell: ({ row }) =>
        puedeExpediente ? (
          <Typography
            component="span"
            onClick={() => navigate(`/prestamos/expediente/${row.noprest}`)}
            sx={{
              fontSize: "inherit",
              fontWeight: 700,
              color: FP.color.accentBlue,
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {row.noprest}
          </Typography>
        ) : (
          row.noprest
        ),
    },
    {
      field: "cliente",
      headerName: "Cliente",
      flex: 1,
      minWidth: 210,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: "inherit" }}>
            {row.cliente}
          </Typography>
          {row.refinanciado && <StatusPill label="Refin." variant="info" />}
        </Stack>
      ),
    },
    { field: "apertura", headerName: "Apertura", width: 100 },
    ...(veSucursales ? [{ field: "sucursal", headerName: "Suc.", width: 70 }] : []),
    moneyCol("costo", "Costo", 112, {
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.4} alignItems="center" justifyContent="flex-end">
          {row.costodudoso && (
            <Tooltip title="Costo en cero o forzado a 1,000: el beneficio puede estar inflado. Corrija el costo del motor.">
              <WarningAmberOutlinedIcon sx={{ fontSize: 16, color: FP.color.warning }} />
            </Tooltip>
          )}
          <span>{fmtMoney(row.costo)}</span>
        </Stack>
      ),
    }),
    moneyCol("precio", "Precio Venta", 118),
    moneyCol("beneficio", "Beneficio"),
    {
      field: "margen",
      headerName: "Margen",
      width: 82,
      type: "number",
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => `${fmtMoney(value, 1)}%`,
    },
    moneyCol("recuperado", "Recuperado", 112, {
      renderCell: ({ row }) => (
        <Tooltip
          title={`Capital del vehículo cobrado: ${fmtMoney(row.capital)}${
            row.cargos ? ` (cargos en cuota excluidos: ${fmtMoney(row.cargos)})` : ""
          }`}
        >
          <span style={{ fontWeight: 700 }}>{fmtMoney(row.recuperado)}</span>
        </Tooltip>
      ),
    }),
    moneyCol("interes", "Interés"),
    moneyCol("mora", "Mora", 100),
    moneyCol("totalrec", "Total Rec.", 118, { cellClassName: "fp-totalrec" }),
    {
      field: "avance",
      headerName: "Avance",
      width: 130,
      sortable: true,
      valueGetter: (_, row) => avancePct(row),
      renderCell: ({ row, value }) => (
        <Tooltip
          title={`Recuperado acumulado ${fmtMoney(row.acumulado)} de ${fmtMoney(
            row.beneficio
          )} · pendiente ${fmtMoney(row.pendiente)}`}
        >
          <Stack direction="row" spacing={0.8} alignItems="center" sx={{ width: "100%" }}>
            <LinearProgress
              variant="determinate"
              value={value}
              sx={{
                flex: 1,
                height: 7,
                borderRadius: 4,
                bgcolor: FP.color.border,
                "& .MuiLinearProgress-bar": { bgcolor: FP.color.success },
              }}
            />
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, minWidth: 30 }}>
              {Math.round(value)}%
            </Typography>
          </Stack>
        </Tooltip>
      ),
    },
  ];

  const hayDatos = filas.length > 0;
  const rd = (v) => `RD$ ${fmtMoney(v)}`;

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Beneficio Recuperado"
        subtitle="Ganancia realizada por los pagos del período: beneficio de venta, interés y mora"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard
              label="Beneficio recuperado"
              value={rd(totales?.recuperado)}
              icon={SavingsOutlinedIcon}
              accent={FP.color.success}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={2.4}>
            <StatCard
              label="Interés"
              value={rd(totales?.interes)}
              icon={PercentOutlinedIcon}
              accent={FP.color.accentBlue}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={2.4}>
            <StatCard
              label="Mora"
              value={rd(totales?.mora)}
              icon={GavelOutlinedIcon}
              accent={FP.color.warning}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard
              label="Ganancia total"
              value={rd(totales?.totalrec)}
              icon={TrendingUpOutlinedIcon}
              accent={FP.color.navy}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard
              label={`Efectivo cobrado · ${totales?.cantidad ?? 0} exp.`}
              value={rd(totales?.cobrado)}
              icon={PaymentsOutlinedIcon}
              accent={FP.color.info}
            />
          </Grid>
        </Grid>

        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Pagos desde"
                InputLabelProps={{ shrink: true }}
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Pagos hasta"
                InputLabelProps={{ shrink: true }}
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2.5}>
              <Autocomplete
                options={sucursales.map((s) => s.label)}
                value={sucursal || null}
                onChange={(_, v) => setSucursal(v || "")}
                disabled={!veSucursales}
                renderInput={(params) => (
                  <TextField {...params} fullWidth label="Sucursal del préstamo" sx={fpInputSx} />
                )}
              />
            </Grid>
            {carteras.length > 0 && (
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Cartera"
                  value={idcartera}
                  onChange={(e) => setIdcartera(e.target.value)}
                  sx={fpInputSx}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {carteras.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.descripcion}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Agrupar impresión"
                value={agrupar}
                onChange={(e) => setAgrupar(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="">Sin agrupar</MenuItem>
                {Object.entries(AGRUPADORES).map(([k, v]) => (
                  <MenuItem key={k} value={k}>
                    {v.etiqueta}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md="auto">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sinRefinanciados}
                    onChange={(e) => setSinRefinanciados(e.target.checked)}
                  />
                }
                label="Excluir refinanciados"
              />
            </Grid>
            <Grid item xs={6} sm={2} md="auto">
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={procesar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
          </Grid>

          <Stack
            direction="row"
            spacing={1.2}
            alignItems="center"
            flexWrap="wrap"
            sx={{ mb: 1.6 }}
            useFlexGap
          >
            {totales?.costodudoso > 0 && (
              <Stack direction="row" spacing={0.6} alignItems="center">
                <WarningAmberOutlinedIcon sx={{ fontSize: 18, color: FP.color.warning }} />
                <Typography sx={{ fontSize: "0.8rem", color: FP.color.textSecondary }}>
                  {totales.costodudoso} expediente(s) con costo dudoso: su beneficio puede estar
                  inflado.
                </Typography>
              </Stack>
            )}
            {hayDatos && !hayLibroCargos && (
              <Typography sx={{ fontSize: "0.8rem", color: FP.color.textMuted }}>
                Sin libro de cargos financiados: todo el capital cobrado cuenta como del vehículo.
              </Typography>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={exportar}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.success)}
            >
              Excel
            </Button>
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={imprimir}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.navy)}
            >
              Imprimir
            </Button>
          </Stack>

          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={filas}
              columns={columnas}
              getRowId={(r) => r.noprest}
              loading={loading}
              rowHeight={44}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{
                ...fpDataGridSx,
                "--DataGrid-overlayHeight": "220px",
                "& .fp-totalrec": { fontWeight: 800, color: FP.color.navy },
              }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
